import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { DEFAULT_HEATMAP_GRADIENT, normalizeHeatmapGradient } from '../utils/heatmapUtils';

const FALLBACK_CONFIG = {
  defaultRadius: 40,
  opacity: 0.85,
  showClustersDefault: true,
  gradient: DEFAULT_HEATMAP_GRADIENT,
};

let heatmapConfigCache = null;
let heatmapConfigPromise = null;

function normalizeConfig(loadedConfig) {
  return {
    defaultRadius:
      loadedConfig.defaultRadius ?? loadedConfig.default_radius ?? FALLBACK_CONFIG.defaultRadius,
    opacity: loadedConfig.opacity ?? FALLBACK_CONFIG.opacity,
    showClustersDefault:
      loadedConfig.showClustersDefault ??
      loadedConfig.show_clusters_default ??
      FALLBACK_CONFIG.showClustersDefault,
    gradient: normalizeHeatmapGradient(loadedConfig.gradient ?? FALLBACK_CONFIG.gradient),
  };
}

export default function useHeatmapConfig() {
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    if (heatmapConfigCache) {
      setConfig(heatmapConfigCache);
      setLoading(false);
      return;
    }

    if (heatmapConfigPromise) {
      try {
        const result = await heatmapConfigPromise;
        setConfig(result);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    heatmapConfigPromise = (async () => {
      try {
        const response = await api.getHeatmapConfig();
        const normalizedConfig = normalizeConfig(response.config || FALLBACK_CONFIG);
        heatmapConfigCache = normalizedConfig;
        setConfig(normalizedConfig);
        return normalizedConfig;
      } catch (err) {
        console.error('Error loading heatmap config:', err);
        setError(err);
        setConfig(FALLBACK_CONFIG);
        return FALLBACK_CONFIG;
      } finally {
        setLoading(false);
        heatmapConfigPromise = null;
      }
    })();

    try {
      await heatmapConfigPromise;
    } catch {
      // Error already handled
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    loading,
    error,
    reloadConfig: loadConfig,
  };
}

export function clearHeatmapConfigCache() {
  heatmapConfigCache = null;
  heatmapConfigPromise = null;
}
